import { useSiteData } from "../hooks/use-site-data";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";

import {
  ClipboardList,
  MapPin,
  AlertTriangle,
  FileText,
  PenTool,
  Eye,
  HeartPulse,
  Table2,
  CalendarDays,
  Users,
  BarChart3,
  AlertCircle,
  CheckSquare,
  BadgeCheck,
  FileWarning,
  PieChart,
  Wrench,
  ListChecks,
  ShieldAlert,
} from "lucide-react";

interface Site {
  id: string;
  name: string;
  location: string;
  template: string;
  status: "active" | "completed" | "pending";
}

interface AuditSection {
  id: string;
  name: string;
  progress: number;
  status: "completed" | "in-progress" | "pending";
  icon?: import("lucide-react").LucideIcon | null;
}

interface AuditContextType {
  selectedSite: Site | null;
  setSelectedSite: (site: Site | null) => void;
  sites: Site[];
  addSite: (site: Site) => void;
  auditSections: AuditSection[];
  updateSectionProgress: (
    sectionId: string,
    progress: number,
    status: string,
  ) => void;
  auditData: Record<string, any>;
  updateAuditData: (sectionId: string, data: any) => void;
  overallProgress: number;
  isAuditComplete: boolean;
}

const AuditContext = createContext<AuditContextType | undefined>(undefined);

const initialMockSites: Site[] = [
  {
    id: "1",
    name: "Solar Farm Alpha",
    location: "Manchester, UK",
    template: "Commercial Solar",
    status: "completed",
  },
  {
    id: "2",
    name: "Rooftop Installation Beta",
    location: "London, UK",
    template: "Residential Solar",
    status: "active",
  },
  {
    id: "3",
    name: "Commercial Site Gamma",
    location: "Birmingham, UK",
    template: "Commercial Solar",
    status: "pending",
  },
  {
    id: "4",
    name: "Industrial Complex Delta",
    location: "Leeds, UK",
    template: "Industrial Solar",
    status: "active",
  },
];

const getInitialSections = (): AuditSection[] => {
  return [
    {
      id: "project-data",
      name: "Project Data",
      progress: 100,
      status: "completed",
      icon: FileText,
    },
    {
      id: "site-data",
      name: "Site Data including CDM",
      progress: 100,
      status: "completed",
      icon: MapPin,
    },
    {
      id: "completed-sample-table",
      name: "Completed Sample Table",
      progress: 100,
      status: "completed",
      icon: Table2,
    },
    {
      id: "delivery-lifting-dno-building",
      name: "Delivery and Lifting of DNO Building",
      progress: 100,
      status: "completed",
      icon: null, // no matching icon in sectionConfig
    },
    {
      id: "mvs-css-lifting",
      name: "MVS and CSS Lifting",
      progress: 100,
      status: "completed",
      icon: null, // no matching icon in sectionConfig
    },
    {
      id: "hse-management",
      name: "HSE management and incident follow up",
      progress: 100,
      status: "completed",
      icon: HeartPulse,
    },
    {
      id: "key-activities-performed",
      name: "Key activities performed for that month and any issues encountered",
      progress: 100,
      status: "completed",
      icon: ClipboardList,
    },
    {
      id: "key-activities-planned",
      name: "Identify key activities for the month ahead",
      progress: 100,
      status: "completed",
      icon: CalendarDays,
    },
    {
      id: "third-party-activities",
      name: "Third Party activities undertaken",
      progress: 100,
      status: "completed",
      icon: Users,
    },
    {
      id: "construction-progress",
      name: "Construction progress against contractual milestones, planned milestones, and critical path items",
      progress: 100,
      status: "completed",
      icon: BarChart3,
    },
    {
      id: "key-risks-and-mitigation",
      name: "Key risks and proposed mitigation",
      progress: 100,
      status: "completed",
      icon: AlertCircle,
    },
    {
      id: "test-and-acceptance",
      name: "Test and acceptance activities and results",
      progress: 100,
      status: "completed",
      icon: CheckSquare,
    },
    {
      id: "payment-certification",
      name: "Certification of payment and contractual milestones",
      progress: 100,
      status: "completed",
      icon: BadgeCheck,
    },
    {
      id: "claims-and-non-conformances",
      name: "Update on claims, variations, and non-conformances raised",
      progress: 100,
      status: "completed",
      icon: FileWarning,
    },
    {
      id: "capex-review",
      name: "Review of spend on technical items compared to the CAPEX and highlighting of any potential budgetary shortfalls because of drawing on contingencies",
      progress: 100,
      status: "completed",
      icon: PieChart,
    },
    {
      id: "snagging-progress",
      name: "Progress with snagging",
      progress: 100,
      status: "completed",
      icon: Wrench,
    },
    {
      id: "payment-milestones-verified",
      name: "Payment milestones verified during each visit or by other updates",
      progress: 100,
      status: "completed",
      icon: null, // id mismatch with `payment-milestones`
    },
    {
      id: "overall-site-observations",
      name: "Overall Site Observations",
      progress: 100,
      status: "completed",
      icon: Eye,
    },
    {
      id: "punch-list",
      name: "Punch List",
      progress: 100,
      status: "completed",
      icon: ListChecks,
    },
    {
      id: "key-issue-matrix",
      name: "Key Issue Matrix (KIM)",
      progress: 100,
      status: "completed",
      icon: AlertTriangle,
    },
    {
      id: "risk-matrix",
      name: "Risk Matrix",
      progress: 100,
      status: "completed",
      icon: ShieldAlert,
    },
    {
      id: "signatures",
      name: "Signatures",
      progress: 100,
      status: "completed",
      icon: PenTool,
    },
  ];
};

export function AuditProvider({ children }: { children: ReactNode }) {
  const [selectedSite, setSelectedSiteState] = useState<Site | null>(null);
  const [sites] = useState<Site[]>(initialMockSites);
  const [auditSections, setAuditSections] =
    useState<AuditSection[]>(getInitialSections());
  const [auditData, setAuditData] = useState<Record<string, any>>({});

  // Load persisted state on mount
  useEffect(() => {
    const savedSiteId = localStorage.getItem("selectedSiteId");
    if (savedSiteId) {
      const site = sites.find((s) => s.id === savedSiteId);
      if (site) {
        setSelectedSiteState(site);
        setAuditSections(getInitialSections());

        // Load audit data
        const savedData = localStorage.getItem(`audit_${savedSiteId}`);
        if (savedData) {
          try {
            setAuditData(JSON.parse(savedData));
          } catch (error) {
            console.error("Failed to load audit data:", error);
          }
        }
      }
    }
  }, [sites]);

  const setSelectedSite = (site: Site | null) => {
    setSelectedSiteState(site);
    if (site) {
      localStorage.setItem("selectedSiteId", site.id);
      setAuditSections(getInitialSections());

      // Load existing audit data for this site
      const savedData = localStorage.getItem(`audit_${site.id}`);
      if (savedData) {
        try {
          setAuditData(JSON.parse(savedData));
        } catch (error) {
          console.error("Failed to load audit data:", error);
          setAuditData({});
        }
      } else {
        setAuditData({});
      }
    } else {
      localStorage.removeItem("selectedSiteId");
      setAuditSections([]);
      setAuditData({});
    }
  };

  const updateSectionProgress = (
    sectionId: string,
    progress: number,
    status: string,
  ) => {
    setAuditSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              progress,
              status: status as "completed" | "in-progress" | "pending",
            }
          : section,
      ),
    );
  };

  const updateAuditData = (sectionId: string, data: any) => {
    const newAuditData = {
      ...auditData,
      [sectionId]: data,
    };
    setAuditData(newAuditData);

    // Persist to localStorage
    if (selectedSite) {
      localStorage.setItem(
        `audit_${selectedSite.id}`,
        JSON.stringify(newAuditData),
      );
    }
  };

  const overallProgress =
    auditSections.length > 0
      ? auditSections.reduce((acc, section) => acc + section.progress, 0) /
        auditSections.length
      : 0;

  const isAuditComplete = overallProgress === 100;

  const {
    siteData,

    create,
  } = useSiteData();

  return (
    <AuditContext.Provider
      value={{
        selectedSite,
        setSelectedSite,
        sites: siteData,
        // :siteData,
        // sites,
        addSite: create,
        auditSections,
        updateSectionProgress,
        auditData,
        updateAuditData,
        overallProgress,
        isAuditComplete,
      }}
    >
      {children}
    </AuditContext.Provider>
  );
}

export function useAudit() {
  const context = useContext(AuditContext);
  if (context === undefined) {
    throw new Error("useAudit must be used within an AuditProvider");
  }

  return context;
}

export const mockSites = initialMockSites;
